/**
 * interior_plan_client.ts — ONE way to get a room's plan (the interiors programme, slice C, 2026-10-01).
 *
 *   planRoom(input, spec) → { result, via, ms }
 *
 * The cache first (`interior_wiring.planCached`'s LRU — a room is planned once a session); a light plan (a house, a
 * shop: a few ms) is made here after ONE yield, so the frame that opened the door has already drawn its shell; a heavy
 * plan (`planIsHeavy`: a tower, a hall) goes to the plan worker. No worker (a bare page, a failed start, a worker
 * error, a worker gone silent) → the synchronous plan after the yield — the room is never left unplanned. Two asks
 * for one room share one plan (`inflight`).
 */
import { planInterior, type PlanInput, type PlanResult } from '../../lib/interior_planner.js';
import { planCachePeek, planCachePut, planIsHeavy, planKey } from '../../lib/interior_wiring.js';
import { yieldToMain } from '../../lib/yield_main.js';

export interface PlannedRoom { result: PlanResult; via: 'cache' | 'sync' | 'worker'; ms: number }

let worker: Worker | null = null;
let workerBroken = false;
let seq = 0;
const waiting = new Map<number, { resolve: (v: { result: PlanResult | null; ms: number; error: string }) => void }>();
const inflight = new Map<string, Promise<PlannedRoom>>();
const stats = { worker: 0, sync: 0, cache: 0, workerErrors: 0, lastError: '' };

/** Ticks of PLAN_SILENT_TICK_MS the PAGE ran with an ask waiting and no word from the worker before it is given up on
 *  (~40× the worst measured plan; counted in ticks, so a frozen page is not a silent worker). */
export const PLAN_SILENT_TICK_MS = 1000;
export const PLAN_SILENT_TICKS = 60;
let quietTicks = 0;
let silences = 0;
let watch: ReturnType<typeof setInterval> | null = null;

// A SILENT worker fires no `error`: its asks waited for ever and `inflight` kept the room's key — it never planned again.
function watchWorker(): void {
  if (waiting.size <= 1) quietTicks = 0; // the only ask waiting starts the count; one queued behind another does not
  if (watch !== null) return;
  const stop = (): void => { if (watch !== null) { clearInterval(watch); watch = null; } };
  watch = setInterval(() => {
    if (waiting.size === 0) { stop(); return; }
    if (++quietTicks < PLAN_SILENT_TICKS) return;
    stop();
    const dead = worker;
    worker = null;                             // the next heavy room starts a fresh worker …
    if (++silences >= 2) workerBroken = true;  // … once: silent twice, every plan is made here from now on
    for (const [, w] of waiting) w.resolve({ result: null, ms: 0, error: 'the plan worker went silent' });
    waiting.clear();
    if (dead) { dead.onmessage = null; dead.onerror = null; try { dead.terminate(); } catch { /* gone already */ } }
  }, PLAN_SILENT_TICK_MS);
}

function startWorker(): Worker | null {
  if (worker || workerBroken) return worker;
  try {
    worker = new Worker(new URL('../../workers/interior_plan_worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<{ id: number; result: PlanResult | null; ms: number; error: string }>): void => {
      quietTicks = 0; // it answered: a worker working through its queue is not silent
      const w = waiting.get(e.data.id);
      if (!w) return;
      waiting.delete(e.data.id);
      w.resolve({ result: e.data.result, ms: e.data.ms, error: e.data.error });
    };
    worker.onerror = (ev): void => {
      // A worker that cannot load or throws outside a job: every waiter falls back to the synchronous plan.
      workerBroken = true; stats.workerErrors++; stats.lastError = String((ev as ErrorEvent).message ?? ev);
      for (const [, w] of waiting) w.resolve({ result: null, ms: 0, error: stats.lastError });
      waiting.clear();
      try { worker?.terminate(); } catch { /* gone already */ }
      worker = null;
    };
  } catch (e) { workerBroken = true; stats.lastError = String(e); worker = null; }
  return worker;
}

function planHere(input: PlanInput): { result: PlanResult; ms: number } {
  const t0 = performance.now();
  const result = planInterior(input);
  return { result, ms: performance.now() - t0 };
}

/** The room's plan: cached, else planned off the door-opening frame. Never rejects. */
export function planRoom(input: PlanInput, spec: { hw: number; hd: number; floors: number }): Promise<PlannedRoom> {
  const hit = planCachePeek(input);
  if (hit) { stats.cache++; return Promise.resolve({ result: hit, via: 'cache', ms: 0 }); }
  const key = planKey(input);
  const running = inflight.get(key);
  if (running) return running;
  const job = (async (): Promise<PlannedRoom> => {
    const w = planIsHeavy(spec) ? startWorker() : null;
    if (w) {
      const id = ++seq;
      const answer = await new Promise<{ result: PlanResult | null; ms: number; error: string }>((resolve) => {
        waiting.set(id, { resolve });
        try { w.postMessage({ id, input }); watchWorker(); } catch (e) { waiting.delete(id); resolve({ result: null, ms: 0, error: String(e) }); }
      });
      if (answer.result) { stats.worker++; planCachePut(input, answer.result, answer.ms); return { result: answer.result, via: 'worker', ms: answer.ms }; }
      stats.workerErrors++; stats.lastError = answer.error; // fall through: plan it here
    }
    await yieldToMain();
    const again = planCachePeek(input); // another ask may have landed while we yielded
    if (again) return { result: again, via: 'cache', ms: 0 };
    const here = planHere(input);
    stats.sync++; planCachePut(input, here.result, here.ms);
    return { result: here.result, via: 'sync', ms: here.ms };
  })();
  inflight.set(key, job);
  void job.finally(() => { if (inflight.get(key) === job) inflight.delete(key); });
  return job;
}

/** Probe lever: how plans were made this session. */
export function planClientStats(): { worker: number; sync: number; cache: number; workerErrors: number; lastError: string; workerUp: boolean; workerBroken: boolean; inflight: number } {
  return { ...stats, workerUp: !!worker, workerBroken, inflight: inflight.size };
}
